/**
 * Oreja - Voice-powered task update system
 *
 * Types shared across the Oreja feature: session management, AI pipeline,
 * clarification Q&A, task proposals, and committed changes.
 */

// ---------------------------------------------------------------------------
// Phase lifecycle
// ---------------------------------------------------------------------------

/** All possible phases of an Oreja session. */
export type OrejaPhase =
  | 'recording'
  | 'uploading'
  | 'transcribing'
  | 'analyzing'
  | 'clarifying'
  | 'answering'
  | 'proposing'
  | 'reviewing'
  | 'refining'
  | 'committing'
  | 'committed';

/** Phases where the session needs user attention (for badge count). */
export const ATTENTION_PHASES: OrejaPhase[] = [
  'clarifying',
  'reviewing',
];

/** Phases where the system is processing (show spinners / progress). */
export const PROCESSING_PHASES: OrejaPhase[] = [
  'transcribing',
  'clarifying',
  'proposing',
  'committing',
];

/** Maximum number of concurrent (non-committed) sessions per user. */
export const MAX_CONCURRENT_SESSIONS = 2;

// ---------------------------------------------------------------------------
// Input mode
// ---------------------------------------------------------------------------

/** How the user provided their update. */
export type OrejaInputMode = 'voice' | 'text';

// ---------------------------------------------------------------------------
// Clarification Q&A
// ---------------------------------------------------------------------------

/** A single clarification question generated by the AI. */
export interface OrejaClarificationQuestion {
  /** Unique id within the session (e.g. "q1"). */
  id: string;
  /** The question text. */
  question: string;
  /** Why this question is being asked. */
  context: string;
  /** Optional multiple-choice answers the user can pick from. */
  options?: string[];
}

// ---------------------------------------------------------------------------
// Task proposals
// ---------------------------------------------------------------------------

/** A single field-level change on an existing ClickUp task. */
export interface OrejaFieldChange {
  /** The field being changed (e.g. "status", "description", "due_date"). */
  field: string;
  /** Previous value (stringified for display). */
  from: string | null;
  /** Proposed new value. */
  to: string | null;
}

/** Proposal to update an existing ClickUp task. */
export interface OrejaUpdatedTaskProposal {
  /** ClickUp task ID to update. */
  clickup_task_id: string;
  /** Task title (for display). */
  task_title: string;
  /** Granular field changes. */
  field_changes: OrejaFieldChange[];
  /** Full enhanced description (if description changed). */
  enhanced_description?: string;
  /** Optional comment to post on the ClickUp task. */
  comment_to_add?: string;
  /** AI confidence in this proposal (0-1). */
  confidence: number;
  /** Brief explanation of why this change is proposed. */
  reasoning: string;
  /** Estimated doc quality score before the change (0-100). */
  doc_quality_before?: number;
  /** Estimated doc quality score after the change (0-100). */
  doc_quality_after?: number;
}

/** Proposal to create a brand-new ClickUp task. */
export interface OrejaNewTaskProposal {
  /** Suggested task title. */
  title: string;
  /** Full task description. */
  description: string;
  /** Suggested status (e.g. "in progress"). */
  suggested_status?: string;
  /** Suggested assignee name. */
  suggested_assignee?: string;
  /** Suggested priority (1=urgent .. 4=low). */
  priority?: string;
  /** Suggested start date (ISO). */
  suggested_start_date?: string;
  /** Suggested due date (ISO). */
  suggested_due_date?: string;
  /** Time estimate string (e.g. "2h", "30m"). */
  time_estimate?: string;
  /** Optional ClickUp list to create in (overrides session default). */
  clickup_list_id?: string;
  /** Optional parent task ID for subtasks. */
  parent_task_id?: string;
  /** AI confidence in this proposal (0-1). */
  confidence: number;
  /** Brief explanation of why this task is proposed. */
  reasoning: string;
  /** Estimated doc quality score (0-100). */
  doc_quality_score?: number;
}

/** Container for all proposals in a session. */
export interface OrejaProposals {
  /** High-level summary of what the AI understood. */
  summary: string;
  /** Proposals to update existing tasks. */
  updated_tasks: OrejaUpdatedTaskProposal[];
  /** Proposals to create new tasks. */
  new_tasks: OrejaNewTaskProposal[];
}

// ---------------------------------------------------------------------------
// Committed changes
// ---------------------------------------------------------------------------

/** Record of a single task that failed to push to ClickUp during commit. */
export interface OrejaFailedCommit {
  /** Whether this was an update or a new task creation. */
  type: 'update' | 'new_task';
  /** Index into the original proposals array (updated_tasks or new_tasks). */
  index: number;
  /** Task title (for display). */
  title: string;
  /** Human-readable error message. */
  error: string;
}

/** Record of a single change that was pushed to ClickUp. */
export interface OrejaCommittedChange {
  /** Whether this was an update or a new task creation. */
  type: 'update' | 'new_task';
  /** ClickUp task ID that was created / updated. */
  clickup_task_id: string;
  /** Full ClickUp URL. */
  clickup_url: string;
  /** Task title (for display). */
  task_title: string;
  /** Human-readable summary of what changed. */
  changes_summary: string;
  /** When the change was pushed (ISO timestamp). */
  pushed_at: string;
}

// ---------------------------------------------------------------------------
// Session record (mirrors DB table)
// ---------------------------------------------------------------------------

/** Full Oreja session row as returned from the database. */
export interface OrejaSession {
  id: string;
  organization_id: string;
  user_id: string;
  client_id: string | null;
  project_id: string | null;
  clickup_list_id: string | null;

  phase: OrejaPhase;

  audio_storage_path: string | null;
  transcript: string | null;
  questions: OrejaClarificationQuestion[] | null;
  answers: string | null;
  proposals: OrejaProposals | null;
  refinement_inputs: string[];
  committed_changes: OrejaCommittedChange[] | null;

  input_mode: OrejaInputMode;
  model_used: string | null;
  created_at: string;
  updated_at: string;
}
